// ── Seller brief ──────────────────────────────────────────────────────────────
//
// The seller as the model read it (app/seller_profile): core business,
// services, customer type, footprint, size, buyer thesis — the four
// sentences a dealmaker reads before a call. Read once per company by the
// daily job (or by Find buyers, or the call-queue prep), refreshed on
// demand, corrected in place; corrections survive a refresh. An
// industry-less company shows the profile's suggested industry with a
// one-click apply (pinned like an edit).
//
// Shared (window.SellerBriefSection, 2026-09-28): the deal page passes
// `deal`; the company and contact pages pass `companyId` — the brief is
// the company's, a deal is only one way to get there.
const SELLER_STEP = { queued: 'Queued…', running: 'Reading the seller’s site and notes…' };
const sellerAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 864e5;
  return d < 1 ? 'today' : d < 2 ? 'yesterday' : d < 30 ? `${Math.floor(d)} days ago` : new Date(iso).toLocaleDateString();
};
const CUSTOMER_TYPES = ['residential', 'commercial', 'both', 'b2b', 'government', 'consumer', 'mixed', 'unknown'];

// `bare` (record-page redesign step 6, a deal guest's Company intel drawer):
// the same body under a plain heading with no fold. `readOnly` (that drawer
// and the v2 brief cards, for a viewer): no Edit / Refresh / Read the seller /
// Set industry. Both default off.
const SellerBriefSection = ({ deal, companyId, sectionId = 'deal.seller_brief', title = 'Seller brief', bare = false, readOnly = false }) => {
  const dealId = deal && deal.id;
  // Seller-only: a buyer pursuit (type buyer_buyside) is about the buyer
  // firm. Not the stage category — buyer shells are force-mapped onto seller
  // stages, so no open engagement ever sits in a 'buyer' stage.
  const sellerSide = deal ? deal.type !== 'buyer_buyside' : true;
  const ref = dealId ? { engagement_id: dealId } : (companyId ? { company_id: companyId } : null);
  const [data, setData] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({});
  const [busy, setBusy] = React.useState(null);

  // A failed poll keeps what is on screen and tries again (a deploy
  // switching instances must not turn a running read into "Not read yet").
  const [pollTick, setPollTick] = React.useState(0);
  const load = React.useCallback(() => {
    if (!ref || !sellerSide) return;
    window.AtlasApi.sellerProfile(ref)
      .then((r) => setData(r || { exists: false }))
      .catch(() => setData((d) => d || { exists: false }))
      .finally(() => setPollTick((x) => x + 1));
  }, [dealId, companyId, sellerSide]);  // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    if (!data || !['queued', 'running'].includes(data.status)) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [data, pollTick, load]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!sellerSide || !ref || !window.AtlasApi.sellerProfile) return null;

  const refresh = async () => {
    setBusy('refresh');
    try { await window.AtlasApi.refreshSellerProfile(dealId ? dealId : ref); setData((d) => ({ ...(d || {}), exists: true, status: 'queued' })); }
    catch (e) { pushToast({ type: 'error', title: 'Could not start the read', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };
  const startEdit = () => {
    const p = (data && data.profile) || {};
    setDraft({ summary: p.summary || '', buyer_thesis: p.buyer_thesis || '', customer_type: p.customer_type || 'unknown',
               footprint: (Array.isArray(p.footprint) ? p.footprint : []).join(', ') });
    setEditing(true);
  };
  const save = async () => {
    setBusy('save');
    try {
      const patch = { summary: draft.summary, buyer_thesis: draft.buyer_thesis, customer_type: draft.customer_type,
                      footprint: draft.footprint.split(',').map((x) => x.trim()).filter(Boolean) };
      const r = await window.AtlasApi.patchSellerProfile(data.company_id, patch);
      // The PATCH reply doesn't look up the industry proposal; keep the GET's.
      setData((d) => ({ ...r, industry_proposal_id: (r && r.industry_proposal_id) || (d && d.industry_proposal_id) || null })); setEditing(false);
      pushToast({ type: 'success', title: 'Seller brief saved', body: 'Your corrections survive the next refresh.' });
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };
  const applyIndustry = async () => {
    setBusy('industry');
    try {
      await window.AtlasApi.updateCompany(data.company_id, { industry_id: data.suggested_industry_id });
      pushToast({ type: 'success', title: `Industry set to ${data.suggested_industry}`, body: 'Reload the page to see it on the company panel.' });
      setData((d) => ({ ...d, company_industry: d.suggested_industry, company_industry_id: d.suggested_industry_id }));
    } catch (e) { pushToast({ type: 'error', title: 'Could not set the industry', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };

  const p = (data && data.profile) || {};
  const running = data && ['queued', 'running'].includes(data.status);
  // The suggested industry opens how it was read (?panel=research&src=industry)
  // when the brief knows its proposal and the page has the research drawer.
  const industryRun = data && data.industry_proposal_id && window.researchDrawerHere && window.researchDrawerHere()
    ? data.industry_proposal_id : null;
  // Narrow headers (v2 side columns and drawers, phone): the read line and the
  // step text move into the body, so the header is Edit + Refresh only. A
  // folded brief that is re-reading shows the step as its preview there (the
  // body is hidden, and the old summary is still in hand).
  const compact = !!(window.headerCompact && window.headerCompact());
  const readLine = data && data.exists && data.read_at && !running ? (
    <span title={(data.pages_read || []).join('\n') || 'Read from Atlas only'}>
      read {sellerAgo(data.read_at)}
      {data.pages_read && data.pages_read.length ? ` from ${data.pages_read.length} page${data.pages_read.length === 1 ? '' : 's'}` : ' from Atlas'}
      {data.edited_at ? ` · edited${data.edited_by ? ` by ${data.edited_by}` : ''}` : ''}
    </span>
  ) : null;
  const action = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-3)' }}>
      {!compact && readLine}
      {!compact && running && <span>{SELLER_STEP[data.status]}</span>}
      {data && data.exists && data.status === 'done' && !editing && !window.AtlasIsGuest && !readOnly && (
        <Btn size="xs" variant="ghost" onClick={startEdit}>Edit</Btn>
      )}
      {!window.AtlasIsGuest && !readOnly && <Btn size="xs" variant="ghost" onClick={refresh} disabled={busy === 'refresh' || Boolean(running)}
        title="Re-read the seller’s website and notes">
        {data && data.exists && data.status === 'done' ? 'Refresh' : 'Read the seller'}
      </Btn>}
    </span>
  );

  const body = (
      <div style={{ padding: bare ? '2px 0 12px' : '12px 14px', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text)' }}>
        {compact && running && <div style={{ color: 'var(--text-3)' }}>{SELLER_STEP[data.status]}</div>}
        {data === null && (bare ? <span style={{ color: 'var(--text-4)' }}>—</span> : <span style={{ color: 'var(--text-3)' }}>Loading…</span>)}
        {data && !data.exists && !running && (bare ? <span style={{ color: 'var(--text-4)' }}>—</span> : (
          <span style={{ color: 'var(--text-3)' }}>
            Not read yet.{window.AtlasIsGuest || readOnly ? '' : <> Press <b>Read the seller</b> to do it now.</>}
          </span>
        ))}
        {data && data.status === 'failed' && (
          <span style={{ color: 'var(--red-fg, #b00)' }}>Last read failed: {data.error}</span>
        )}
        {data && data.status === 'done' && !editing && (
          <>
            <div>{p.summary}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
              {p.customer_type && <Pill hue="blue" sz="sm">{p.customer_type} customers</Pill>}
              {(Array.isArray(p.footprint) ? p.footprint : []).slice(0, 5).map((f) => <Pill key={f} hue="slate" sz="sm">{f}</Pill>)}
              {p.size_notes && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{p.size_notes}</span>}
            </div>
            {Array.isArray(p.services) && p.services.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 8 }}>
                <b style={{ color: 'var(--text-3)', fontWeight: 500 }}>Services:</b> {p.services.slice(0, 10).join(' · ')}
              </div>
            )}
            {p.buyer_thesis && (
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>
                <b style={{ color: 'var(--text-3)', fontWeight: 500 }}>Buyer thesis:</b> {p.buyer_thesis}
              </div>
            )}
            {!data.company_industry_id && data.suggested_industry_id && !window.AtlasIsGuest && !readOnly && (
              <div style={{ marginTop: 10, padding: '8px 10px', border: '1px dashed var(--border)', borderRadius: 6,
                            display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                <span>This company has no industry. The brief reads as {industryRun ? (
                  <button type="button" onClick={() => window.openResearchRun({ src: 'industry', run: data.industry_proposal_id })} title="How it got here"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{data.suggested_industry}</button>
                ) : <b>{data.suggested_industry}</b>} (also proposed on Cleanup).</span>
                <Btn size="xs" onClick={applyIndustry} disabled={busy === 'industry'}>Set industry</Btn>
              </div>
            )}
          </>
        )}
        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 11, color: 'var(--text-3)' }}>Summary
              <textarea value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} rows={4}
                style={{ width: '100%', fontFamily: 'inherit', fontSize: 12.5, padding: 8, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }} />
            </label>
            <label style={{ fontSize: 11, color: 'var(--text-3)' }}>Buyer thesis
              <textarea value={draft.buyer_thesis} onChange={(e) => setDraft({ ...draft, buyer_thesis: e.target.value })} rows={3}
                style={{ width: '100%', fontFamily: 'inherit', fontSize: 12.5, padding: 8, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }} />
            </label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 11, color: 'var(--text-3)' }}>Customer type<br />
                <window.Picker value={draft.customer_type} onChange={(v) => setDraft({ ...draft, customer_type: v })} size="sm" placeholder="Customer type"
                  minWidth={120} options={CUSTOMER_TYPES} />
              </label>
              <label style={{ fontSize: 11, color: 'var(--text-3)', flex: 1 }}>Footprint (comma-separated)<br />
                <input value={draft.footprint} onChange={(e) => setDraft({ ...draft, footprint: e.target.value })}
                  style={{ width: '100%', fontSize: 12, padding: 4, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="xs" onClick={save} disabled={busy === 'save'}>{busy === 'save' ? 'Saving…' : 'Save'}</Btn>
              <Btn size="xs" variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn>
            </div>
          </div>
        )}
        {compact && readLine && !editing && (
          <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.4, color: 'var(--text-4)' }}>{readLine}</div>
        )}
      </div>
  );
  // bare: the Company intel drawer (views/company_intel.jsx): the same body under a plain heading, no fold.
  if (bare) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px', minWidth: 0 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', flexShrink: 0 }}>{title}</h3>
          <div style={{ marginLeft: 'auto', minWidth: 0 }}>{action}</div>
        </div>
        {body}
      </div>
    );
  }
  return (
    <Section title={title} action={action} collapsible id={sectionId} defaultOpen={false}
      preview={compact && running ? SELLER_STEP[data.status] : p.summary ? String(p.summary).split(/(?<=\.)\s+/)[0] : (running ? SELLER_STEP[data.status] : (data && !data.exists ? 'Not read yet' : null))}>
      {body}
    </Section>
  );
};

window.SellerBriefSection = SellerBriefSection;

// ── Company intel card (record-page redesign step 6) ──────────────────────────
// The v2 record pages swap the Seller brief slot for this card (the page slots
// read window.recordCardsOn('IntelCard')). The seller brief stays apart from the
// firmographic intel (Decisions 2026-10-13: the brief belongs with MyExit as the
// seller's exit profile): the card renders the classic folded brief under its
// *.seller_brief id, then its own folded Company intel Section (deal.intel /
// company.intel) fed by the record summary (views/record_summary.jsx): the size
// tier and the headcount, founded and ownership chips. The brief keeps its own
// Read the seller. Facts & fit, Firmographics and the description open in the
// Company intel drawer (?panel=intel, views/company_intel.jsx). A deal guest
// gets the brief alone.
const intelTierLabel = (tier) => {
  if (!tier) return 'Tier —';
  const t = window.COMPANY_TIER;      // company_facts.jsx; read at call time
  return (t && t.label && t.label[tier]) || String(tier);
};
const intelCount = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
// The folded line's parts: tier · headcount · founded, each dimmed when
// unknown; [] while loading or when nothing is known (the card then shows a
// dimmed —).
const intelPreviewParts = ({ tier, facts, loading = false } = {}) => {
  if (loading) return [];
  const f = facts || {};
  const parts = [
    tier ? { text: intelTierLabel(tier), dim: false } : { text: 'Tier —', dim: true },
    f.headcount ? { text: `${intelCount(f.headcount)} people`, dim: false } : { text: '— people', dim: true },
    f.founded ? { text: `founded ${f.founded}`, dim: false } : { text: 'founded —', dim: true },
  ];
  return parts.some((x) => !x.dim) ? parts : [];
};
window.IntelKit = Object.freeze({ tierLabel: intelTierLabel, count: intelCount, previewParts: intelPreviewParts });

const INTEL_PILL_BTN = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' };
const INTEL_CHIP = { display: 'inline-flex', alignItems: 'center', fontSize: 11.5, color: 'var(--text-2)', padding: '1px 8px',
  border: '1px solid var(--border)', borderRadius: 999, whiteSpace: 'nowrap', background: 'none', font: 'inherit' };
const INTEL_DIM = { color: 'var(--text-4)' };
const intelOpenFacts = () => window.openRecordPanel('intel', { focus: 'facts' }, { via: 'button' });

// The tier pill: a verdict opens how it was reached (research src=tier); no
// verdict opens the drawer at Facts & fit, where Qualify is one click (never
// a paid action from a pill).
const IntelTierPill = ({ tier }) => {
  const T = window.COMPANY_TIER || { label: {}, hue: {} };
  if (!tier) {
    return <button type="button" onClick={intelOpenFacts} style={INTEL_PILL_BTN}><window.Pill hue="slate" sz="sm">Tier —</window.Pill></button>;
  }
  return (
    <button type="button" title="How it got here" style={INTEL_PILL_BTN}
      onClick={() => { if (!(window.openResearchRun && window.openResearchRun({ src: 'tier' }))) intelOpenFacts(); }}>
      <window.Pill hue={(T.hue && T.hue[tier]) || 'slate'} sz="sm">{intelTierLabel(tier)}</window.Pill>
    </button>
  );
};

const IntelCard = ({ deal, companyId, sectionId = 'deal.seller_brief', variant = 'card' }) => {
  const kind = deal ? 'deal' : 'company';
  const rid = deal ? deal.id : companyId;
  const guest = !!window.AtlasIsGuest;
  const applies = !!rid && !(deal && (deal.type === 'buyer_buyside' || !deal.target));
  // A deal guest's summary carries no tier or facts: no read for the brief alone.
  const sum = window.useRecordSummary(kind, rid, { enabled: applies && !guest });
  if (!applies) return null;
  const canWrite = window.recordCanWrite();
  const brief = <window.SellerBriefSection deal={deal} companyId={companyId} sectionId={sectionId} readOnly={!canWrite} />;
  if (guest) return brief;
  const s = sum.data || null;
  const b = (s && s.brief) || null;
  const facts = (s && s.facts) || null;
  const own = (s && s.ownership) || null;
  const tier = b && b.tier;
  const parts = intelPreviewParts({ tier, facts, loading: sum.loading });
  const line = parts.length
    ? parts.map((x, i) => <React.Fragment key={i}>{i ? ' · ' : ''}<span style={x.dim ? INTEL_DIM : undefined}>{x.text}</span></React.Fragment>)
    : <span style={INTEL_DIM}>—</span>;
  const expand = window.recordPanelAvailable('intel', window.recordSurface()) ? (
    <window.Btn size="xs" variant="ghost" title="Open" icon={<window.Icon.Maximize size={11} />} onClick={intelOpenFacts} />
  ) : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: variant === 'rail' ? 0 : 14 }}>
      {brief}
      <window.Section variant={variant} title="Company intel" action={expand}
        collapsible id={sectionId.replace('seller_brief', 'intel')} defaultOpen={false} preview={line}>
        <div style={{ padding: '10px 14px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text)' }}>
          <IntelTierPill tier={tier} />
          <span style={INTEL_CHIP} title={facts && facts.headcount_source === 'sourcescrub' ? 'SourceScrub' : 'LinkedIn'}>
            {facts && facts.headcount ? `${intelCount(facts.headcount)} people` : <span style={INTEL_DIM}>— people</span>}
          </span>
          <span style={INTEL_CHIP}>{facts && facts.founded ? `founded ${facts.founded}` : <span style={INTEL_DIM}>founded —</span>}</span>
          {own && own.outcome_label ? (
            <button type="button" style={{ ...INTEL_CHIP, cursor: 'pointer' }}
              onClick={() => window.openResearchRun && window.openResearchRun({ src: 'ownership' })}>{own.outcome_label}</button>
          ) : <span style={{ ...INTEL_CHIP, ...INTEL_DIM }}>ownership —</span>}
        </div>
      </window.Section>
    </div>
  );
};
window.IntelCard = IntelCard;
window.IntelTierPill = IntelTierPill;
